import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { DocumentTypeEntity } from '../../distribution/models';
import { GenericModalService, ModalSettingsTemplate } from '../../shared/generic-modal/generic-modal.service';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { Direction } from '../models/direction';
import { DocumentConfig } from '../models/document-config';
import { Format } from '../models/format';
import { ItineraryStep } from '../models/itinerary';
import { Variant } from '../models/variant';
import { VariantVersion } from '../models/variant-version';
import { DeveloperService } from '../services/developer.service';
import { DeveloperDocumentConfigBatchingComponent } from './developer-document-configs-batching.component';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule, DxDropDownBoxModule } from 'devextreme-angular';
import { DxoColumnFixingModule, DxoEditingModule, DxoPopupModule, DxiColumnModule, DxoLookupModule, DxiValidationRuleModule, DxiButtonModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';

import { DeveloperDocumentConfigItineraryComponent } from './developer-document-configs-itinerary.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';


@Component({
    selector: 'app-developer-document-configs',
    templateUrl: './developer-document-configs.component.html',
    styleUrls: ['./developer-document-configs.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoColumnFixingModule, DxoEditingModule, DxoPopupModule, DxiColumnModule, DxoLookupModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, DxDropDownBoxModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule, DeveloperDocumentConfigItineraryComponent]
})
export class DeveloperDocumentConfigsComponent implements OnInit, AfterViewInit {
    distros: DocumentTypeEntity[] = [];
    formats: Format[] = [];
    variants: Variant[] = [];
    versions: VariantVersion[] = [];
    directions: Direction[] = [{ DirectionId: 'In', DirectionName: 'Inbound' }, { DirectionId: 'Out', DirectionName: 'Outbound' }];
    itinerarySteps: ItineraryStep[] = [];
    configs: DocumentConfig[] = [];
    configToCopy: DocumentConfig = null;
    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    allowCopying = false;
    allowShowLog = false;
    dropDownOptions = { width: 500 };
    currentEditingItineraryName = '';
    currentEditingItineraryDocumentConfigId: number = null;
    showEditItinerary = false;
    isNew = false;
    isCopy = false;
    @ViewChild(BizDataGridDirective, { static: true }) private datagrid: BizDataGridDirective;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private router: Router,
        private route: ActivatedRoute,
        private modalService: GenericModalService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {
        this.editItinerary = this.editItinerary.bind(this);
        this.configureBatchingTypes = this.configureBatchingTypes.bind(this);
        this.getDocumentTypeName = this.getDocumentTypeName.bind(this);
        this.getFormatName = this.getFormatName.bind(this);
        this.getVariantName = this.getVariantName.bind(this);
        this.getVersionName = this.getVersionName.bind(this);
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_DocumentConfig_Create');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_DocumentConfig_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_DocumentConfig_Delete');
        this.allowCopying = this.currentUserService.hasPermission('Developer_Action_DocumentConfig_Copy');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');

        this.route.params.subscribe(async(parms: any) => {
            await this.refresh();

            if (parms.selectedDocumentConfigId) {
                setTimeout(() => {
                    const selectedConfig = this.configs.find(c => c.DocumentConfigId == +parms.selectedDocumentConfigId)

                    if (selectedConfig) {
                        if (parms.documentConfigShowType == 'itinerary') {
                            this.editItinerarayInternal(selectedConfig);
                        }
                        else if (parms.documentConfigShowType == 'entity') {
                            const rowIndex = this.datagrid.instance.getRowIndexByKey(selectedConfig);
                            this.datagrid.instance.editRow(rowIndex);
                        }
                    }
                }, 700); // It seems like the grid hasn't detected the rows at this point - the getRowIndexByKey always returns -1. But by giving it some time, it seems to work... :-/
            }
        });
    }

    onEditorPreparing(e) {
        if (e.dataField === 'Direction' && e.parentType === 'dataRow') {
            e.editorOptions.disabled = e.row.data.LockDirection;
        }
    }

    getIsNew(): boolean {
        return this.isNew;
    }

    initializeContextMenu() {
        this.datagrid.instance.option('onContextMenuPreparing', (e) => {
            if (e.row.rowType === 'data') {
                e.items = [];
                const docConfig = e.row.data as DocumentConfig;

                if (this.currentUserService.hasPermission('Distribution_View_Search')) {
                    e.items.push({
                        text: 'Search for distributions with this configuration',
                        onItemClick: () => {
                            this.loadingPanelService.showLoadingPanelDirect('Navigating to Distribution...');
                            let searchParams: any = { splitFromAndTo: true };
                            if (docConfig.Direction == "In") {
                                searchParams.documentConfigFrom = docConfig.DocumentConfigId;
                                searchParams.formatFromId = docConfig.FormatId;
                                searchParams.variantFromId = docConfig.VariantId;
                                searchParams.versionFromId = docConfig.VariantVersionsId;
                            }
                            else {
                                searchParams.documentConfigTo = docConfig.DocumentConfigId;
                                searchParams.formatToId = docConfig.FormatId;
                                searchParams.variantToId = docConfig.VariantId;
                                searchParams.versionToId = docConfig.VariantVersionsId;
                            }

                            this.router.navigate(['distribution/result', searchParams]);
                            this.loadingPanelService.hideLoadingPanel();
                        }
                    });
                }
            }
        });
    }

    ngAfterViewInit() {
        this.initializeContextMenu();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.configs = [];

            let configs: DocumentConfig[] = [];
            await Promise.all([
                this.developerService.getDocumentTypes().then(result => { this.distros = result; }),
                this.developerService.getFormats().then(result => { this.formats = result.sort((a, b) => a.FormatName.localeCompare(b.FormatName)); }),
                this.developerService.getVariants().then(result => { this.variants = result.sort((a, b) => a.VariantName.localeCompare(b.VariantName)); }),
                this.developerService.getVariantVersions().then(result => { this.versions = result.sort((a, b) => a.VariantVersionName.localeCompare(b.VariantVersionName)); }),
                this.developerService.getItinerarySteps().then(result => { this.itinerarySteps = result; }),
                this.developerService.getDocumentConfigs().then(result => { configs = result; })
            ]);

            // Ensure that these are set last
            this.configs = configs;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        if (this.configToCopy != null) {
            e.data = {
                DocumentConfigId: this.configToCopy.DocumentConfigId, // Not used in UI, but must be returned to back-end on updates
                DistributionObjectId: this.configToCopy.DistributionObjectId,
                FormatId: this.configToCopy.FormatId,
                VariantId: this.configToCopy.VariantId,
                VariantVersionsId: this.configToCopy.VariantVersionsId,
                Direction: this.configToCopy.Direction,
                Description: this.configToCopy.Description
            } as DocumentConfig;
            this.configToCopy = null;
        }
        else {
            this.isNew = true;
            e.data = {
                DocumentConfigId: null, // Not used in UI, but must be returned to back-end on updates
                DistributionObjectId: null,
                FormatId: null,
                VariantId: null,
                VariantVersionsId: null,
                Direction: 'In',
                Description: ''
            } as DocumentConfig;
        }
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as DocumentConfig, item => this.saveDocumentConfig(item, this.isCopy));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as DocumentConfig, item => this.saveDocumentConfig(item, false), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as DocumentConfig, item => this.removeDocumentConfig(item));
    };

    onRowCopying = (e: any): void => {
        this.isCopy = true;
        this.configToCopy = e.row.data;
        this.datagrid.instance.addRow();
    };

    onEditCanceled = (e: any): void => {
        this.isCopy = false;
    };

    async saveDocumentConfig(config: DocumentConfig, isCopy: boolean): Promise<DocumentConfig> {
        const newConfig = await this.developerService.saveDocumentConfig(config, isCopy);
        this.toastService.showToast('Document configuration saved...', ToastType.success);
        this.isNew = false;
        this.isCopy = false;
        return newConfig;
    }

    async removeDocumentConfig(config: DocumentConfig) : Promise<DocumentConfig> {
        await this.developerService.deleteDocumentConfig(config.DocumentConfigId);
        this.toastService.showToast('Document configuration deleted...', ToastType.success);
        return config;
    }

    getItinerarySteps(cellInfo: any) {
        return [];
    }

    // For use with grid in drop down template
    onSelectionChanged(selectedRowKeys, cellInfo, dropDownBoxComponent): void {
        cellInfo.setValue(selectedRowKeys[0]);
        if (selectedRowKeys.length > 0) {
            dropDownBoxComponent.close();
        }
    }

    getDocumentTypeName(documentConfig: DocumentConfig) {
        return this.distros.find(d => d.Id == documentConfig.DistributionObjectId)?.Name || 'Unknown';
    }

    getFormatName(documentConfig: DocumentConfig) {
        return this.formats.find(d => d.FormatsId == documentConfig.FormatId)?.FormatName || 'Unknown';
    }

    getVariantName(documentConfig: DocumentConfig) {
        return this.variants.find(d => d.VariantsId == documentConfig.VariantId)?.VariantName || 'Unknown';
    }

    getVersionName(documentConfig: DocumentConfig) {
        return this.versions.find(d => d.VariantVersionsId == documentConfig.VariantVersionsId)?.VariantVersionName || 'Unknown';
    }

    editItinerary(e: any) {
        const docConfig = e.row.data as DocumentConfig;
        this.editItinerarayInternal(docConfig);
    }

    async configureBatchingTypes(e: any) { 
        const docConfig = e.row.data as DocumentConfig;
        const documentConfigOutId = docConfig.DocumentConfigId;
        var componentValues = { documentConfigOutId };
        
        try {
            const modalSettings = new ModalSettingsTemplate('lg', 'static');
            await this.modalService.openComponent(DeveloperDocumentConfigBatchingComponent, componentValues, modalSettings);
        } catch (error) {
        }
    }

    isRowDocumentConfigOut(e: any) {
        const docConfig = e.row.data as DocumentConfig;
        return docConfig.Direction == 'Out'
    }

    editItinerarayInternal(docConfig: DocumentConfig) {
        const distributionObjectName = this.distros.find(d => d.Id === docConfig.DistributionObjectId)?.Name;
        const formatName = this.formats.find(d => d.FormatsId === docConfig.FormatId)?.FormatName;
        const variantName = this.variants.find(d => d.VariantsId === docConfig.VariantId)?.VariantName;
        const versionName = this.versions.find(d => d.VariantVersionsId === docConfig.VariantVersionsId)?.VariantVersionName;
        const directionName = this.directions.find(d => d.DirectionId === docConfig.Direction)?.DirectionName;

        this.currentEditingItineraryName = `${distributionObjectName} - ${formatName} / ${variantName} / ${versionName} / ${directionName}`;
        this.showEditItinerary = true;
        this.currentEditingItineraryDocumentConfigId = docConfig.DocumentConfigId;
    }



  
}
